import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiArrowLeft,
  FiBox,
  FiEdit2,
  FiImage,
  FiLayers,
  FiPackage,
  FiPlus,
  FiSave,
  FiShoppingBag,
  FiTag,
  FiTrash2,
  FiTruck,
  FiUpload,
} from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { apiRequest } from "../../../services/api";
import ProductVariantModal from "../../../components/masters/products/ProductVariantModal";
import { printVariantBarcodeLabels } from "../../../utils/printBarcodeLabels";

const money = (value) =>
  value === null || value === undefined || value === ""
    ? "—"
    : `₹${Number(value).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

const formatRuleGroup = (value) =>
  String(value || "GENERAL")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const statusStyles = {
  ACTIVE: "bg-green-100 text-green-700",
  DRAFT: "bg-yellow-100 text-yellow-700",
  INACTIVE: "bg-gray-100 text-gray-700",
  ARCHIVED: "bg-red-100 text-red-700",
};

const lotSummary = (variant) => {
  const lots = variant?.inventoryLots || [];
  const availableQuantity = lots.reduce(
    (total, lot) =>
      total +
      Math.max(
        0,
        Number(lot.remainingQuantity || 0) - Number(lot.reservedQuantity || 0),
      ),
    0,
  );

  return { activeLot: lots[0] || null, availableQuantity };
};

const getImageUrl = (storageKey) => {
  if (!storageKey) return "";

  if (
    storageKey.startsWith("http://") ||
    storageKey.startsWith("https://")
  ) {
    return storageKey;
  }

  const baseUrl = (
    process.env.REACT_APP_ASSET_URL ||
    process.env.REACT_APP_API_URL ||
    ""
  ).replace(/\/$/, "");

  return `${baseUrl}${storageKey.startsWith("/") ? "" : "/"}${storageKey}`;
};

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { accessToken, isLoading: isAuthLoading } = useAuth();

  const [product, setProduct] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [variantModal, setVariantModal] = useState({
    open: false,
    variant: null,
  });
  const [reprintVariant, setReprintVariant] = useState(null);
  const [reprintQuantity, setReprintQuantity] = useState("1");
  const [reprintError, setReprintError] = useState("");

  const [draftImages, setDraftImages] = useState([]);
  const [activeImage, setActiveImage] = useState(0);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [savingImages, setSavingImages] = useState(false);
  const [imageError, setImageError] = useState("");

  const loadProduct = useCallback(async () => {
    if (!accessToken || !id) return;

    try {
      setLoading(true);
      setError("");

      const productData = await apiRequest(`/products/${id}`, {
        accessToken,
      });

      setProduct(productData);

      const sortedImages = [...(productData.images || [])].sort((a, b) => {
        const aThumbnail = a.role === "THUMBNAIL" ? 0 : 1;
        const bThumbnail = b.role === "THUMBNAIL" ? 0 : 1;

        if (aThumbnail !== bThumbnail) {
          return aThumbnail - bThumbnail;
        }

        return Number(a.sortOrder || 0) - Number(b.sortOrder || 0);
      });

      setDraftImages(sortedImages);
      setActiveImage(0);

      try {
        const variantBalances = await Promise.all(
          (productData.variants || []).map((variant) =>
            apiRequest(`/inventory/balances?variantId=${variant.id}`, {
              accessToken,
            }),
          ),
        );

        setInventory(variantBalances.flat());
      } catch {
        setInventory([]);
      }
    } catch (err) {
      setError(err.message || "Unable to load product.");
    } finally {
      setLoading(false);
    }
  }, [accessToken, id]);

  useEffect(() => {
    if (isAuthLoading || !accessToken) return;

    loadProduct();
  }, [isAuthLoading, accessToken, loadProduct]);

  const totalStock = useMemo(
    () =>
      inventory.reduce(
        (total, balance) => total + Number(balance.quantityOnHand || 0),
        0,
      ),
    [inventory],
  );

  const totalReserved = useMemo(
    () =>
      inventory.reduce(
        (total, balance) => total + Number(balance.quantityReserved || 0),
        0,
      ),
    [inventory],
  );

  const variants = product?.variants || [];
  const suppliers = product?.suppliers || [];
  const images = draftImages;

  const tabs = [
    { id: "overview", label: "Overview", icon: <FiPackage /> },
    { id: "variants", label: "Variants", icon: <FiLayers /> },
    { id: "inventory", label: "Inventory", icon: <FiBox /> },
    { id: "suppliers", label: "Suppliers", icon: <FiTruck /> },
    { id: "images", label: "Images", icon: <FiImage /> },
  ];

  const deleteVariant = async (variant) => {
    const confirmed = window.confirm(`Deactivate variant "${variant.sku}"?`);

    if (!confirmed) return;

    try {
      await apiRequest(`/products/${id}/variants/${variant.id}`, {
        method: "DELETE",
        accessToken,
      });

      await loadProduct();
    } catch (err) {
      setError(err.message || "Unable to deactivate variant.");
    }
  };

  const uploadSelectedImages = async (event) => {
    const files = Array.from(event.target.files || []);

    if (!files.length) return;

    if (images.length + files.length > 8) {
      setImageError("You can add a maximum of 8 images per product.");
      event.target.value = "";
      return;
    }

    try {
      setUploadingImages(true);
      setImageError("");

      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);

        const uploaded = await apiRequest("/uploads/products", {
          method: "POST",
          body: formData,
          accessToken,
        });

        setDraftImages((current) => [
          ...current,
          {
            storageKey: uploaded.storageKey,
            altText: "",
            role: current.length === 0 ? "THUMBNAIL" : "GALLERY",
            sortOrder: current.length,
          },
        ]);
      }
    } catch (err) {
      setImageError(err.message || "Unable to upload product images.");
    } finally {
      setUploadingImages(false);
      event.target.value = "";
    }
  };

  const removeImage = (storageKey) => {
    setDraftImages((current) => {
      const removedImage = current.find(
        (image) => image.storageKey === storageKey,
      );

      const remaining = current.filter(
        (image) => image.storageKey !== storageKey,
      );

      return remaining.map((image, index) => ({
        ...image,
        role:
          removedImage?.role === "THUMBNAIL" && index === 0
            ? "THUMBNAIL"
            : image.role,
        sortOrder: index,
      }));
    });

    setActiveImage(0);
  };

  const setMainImage = (storageKey) => {
    setDraftImages((current) =>
      current.map((image, index) => ({
        ...image,
        role:
          image.storageKey === storageKey
            ? "THUMBNAIL"
            : image.role === "THUMBNAIL"
              ? "GALLERY"
              : image.role,
        sortOrder: index,
      })),
    );

    setActiveImage(0);
  };

  const updateImageAltText = (storageKey, altText) => {
    setDraftImages((current) =>
      current.map((image) =>
        image.storageKey === storageKey ? { ...image, altText } : image,
      ),
    );
  };

  const saveImages = async () => {
    try {
      setSavingImages(true);
      setImageError("");

      const updatedProduct = await apiRequest(`/products/${id}/images`, {
        method: "PATCH",
        accessToken,
        body: {
          images: images.map((image, index) => ({
            storageKey: image.storageKey,
            role: image.role,
            altText: image.altText?.trim() || undefined,
            sortOrder: index,
          })),
        },
      });

      setProduct(updatedProduct);
      setDraftImages(updatedProduct.images || []);
      setActiveImage(0);
    } catch (err) {
      setImageError(err.message || "Unable to save product images.");
    } finally {
      setSavingImages(false);
    }
  };

  if (loading || isAuthLoading) {
    return (
      <div className="py-12 text-center text-gray-500">Loading product…</div>
    );
  }

  if (error || !product) {
    return (
      <div className="rounded-2xl bg-red-50 p-6 text-red-700">
        <p>{error || "Product not found."}</p>

        <button
          onClick={() => navigate("/admindashboard/masters/products")}
          className="mt-4 rounded-xl bg-primary-700 px-4 py-2 text-white"
        >
          Back to Products
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex gap-4">
          <button
            onClick={() => navigate("/admindashboard/masters/products")}
            className="mt-1 flex h-10 w-10 items-center justify-center rounded-xl bg-white text-gray-600 shadow-sm hover:bg-gray-100"
          >
            <FiArrowLeft />
          </button>

          <div>
            <p className="text-sm text-gray-500">
              Masters / Products / Product Profile
            </p>

            <div className="mt-1 flex flex-wrap items-center gap-3">
              <h1 className="text-3xl font-bold text-gray-900">
                {product.name}
              </h1>

              <span
                className={`rounded-full px-3 py-1 text-xs font-medium ${
                  statusStyles[product.status] || "bg-gray-100 text-gray-700"
                }`}
              >
                {product.status}
              </span>
            </div>

            <p className="mt-2 text-gray-500">
              Product Code: {product.productCode || "—"} · {variants.length}{" "}
              variant{variants.length === 1 ? "" : "s"}
            </p>
          </div>
        </div>

        <button
          onClick={() =>
            navigate(`/admindashboard/masters/products/${product.id}/edit`)
          }
          className="flex h-11 items-center justify-center gap-2 rounded-xl bg-primary-700 px-5 text-white hover:bg-primary-800"
        >
          <FiEdit2 />
          Edit Product
        </button>
      </div>

      <div className="mb-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Variants"
          value={variants.length}
          icon={<FiLayers />}
          color="bg-primary-50 text-primary-700"
        />

        <StatCard
          label="Stock On Hand"
          value={totalStock.toLocaleString("en-IN")}
          icon={<FiBox />}
          color="bg-blue-50 text-blue-700"
        />

        <StatCard
          label="Reserved Stock"
          value={totalReserved.toLocaleString("en-IN")}
          icon={<FiShoppingBag />}
          color="bg-orange-50 text-orange-600"
        />

        <StatCard
          label="Product Images"
          value={images.length}
          icon={<FiImage />}
          color="bg-green-50 text-green-700"
        />
      </div>

      <div className="mb-6 flex gap-2 overflow-x-auto rounded-2xl border border-gray-100 bg-white p-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex h-11 items-center gap-2 whitespace-nowrap rounded-xl px-4 text-sm font-medium ${
              activeTab === tab.id
                ? "bg-primary-700 text-white"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "overview" && (
        <div className="grid gap-6 lg:grid-cols-3">
          <section className="rounded-3xl border border-gray-100 bg-white p-6 lg:col-span-2">
            <h2 className="mb-5 text-xl font-bold">Product Information</h2>

            <div className="grid gap-5 sm:grid-cols-2">
              <Info label="Brand" value={product.brand?.name} />
              <Info label="Category" value={product.category?.name} />
              <Info label="Subcategory" value={product.subCategory?.name} />
              <Info
                label="Unit"
                value={
                  product.unit
                    ? `${product.unit.name}${
                        product.unit.symbol ? ` (${product.unit.symbol})` : ""
                      }`
                    : null
                }
              />
              <Info label="Season" value={product.season?.name} />
              <Info label="HSN Code" value={product.hsnCode} />
              <Info
                label="GST Rule Group"
                value={
                  product.gstSlab?.ruleGroup
                    ? `${formatRuleGroup(product.gstSlab.ruleGroup)} · Automatic by final item value`
                    : null
                }
              />
              <Info label="Slug" value={product.slug} />
            </div>

            <div className="mt-6 border-t border-gray-100 pt-6">
              <p className="mb-2 text-sm font-medium text-gray-700">
                Description
              </p>

              <p className="whitespace-pre-line text-sm leading-6 text-gray-600">
                {product.description ||
                  product.shortDescription ||
                  "No description added."}
              </p>
            </div>
          </section>

          <section className="rounded-3xl border border-gray-100 bg-white p-6">
            <h2 className="mb-5 text-xl font-bold">Product Settings</h2>

            <div className="space-y-4">
              <Setting label="Track Inventory" value={product.trackInventory} />
              <Setting
                label="Variant-Level Tracking"
                value={product.variantLevelTracking}
              />
              <Setting
                label="Allow Negative Stock"
                value={product.allowNegativeStock}
              />
              <Setting
                label="Auto Reorder Alerts"
                value={product.autoReorderAlerts}
              />
              <Setting label="Online Visible" value={product.isOnlineVisible} />
              <Setting label="Featured Product" value={product.isFeatured} />
              <Setting label="New Arrival" value={product.isNewArrival} />
              <Setting label="Best Seller" value={product.isBestSeller} />
              <Setting label="Allow Reviews" value={product.allowReviews} />
            </div>
          </section>
        </div>
      )}

      {activeTab === "variants" && (
        <section className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
          <div className="flex items-center justify-between border-b border-gray-100 p-6">
            <h2 className="text-xl font-bold">Product Variants</h2>

            <button
              onClick={() =>
                setVariantModal({
                  open: true,
                  variant: null,
                })
              }
              className="flex h-11 items-center gap-2 rounded-xl bg-primary-700 px-4 text-white"
            >
              <FiPlus />
              Add Variant
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-gray-50">
                  {[
                    "Variant",
                    "SKU",
                    "CHARMS barcode",
                    "Supplier / EAN barcode",
                    "Color",
                    "Size",
                    "Lot Pricing",
                    "Available",
                    "Status",
                    "Actions",
                  ].map((heading) => (
                    <th
                      key={heading}
                      className="px-6 py-4 text-left text-xs font-medium uppercase text-gray-500"
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {variants.map((variant) => {
                  const { activeLot, availableQuantity } = lotSummary(variant);

                  return (
                  <tr key={variant.id} className="border-b border-gray-100">
                    <td className="px-6 py-4 font-medium">
                      {variant.variantName || "Default"}
                    </td>
                    <td className="px-6 py-4 text-sm">{variant.sku}</td>
                    <td className="px-6 py-4 text-sm">
                      {variant.barcode || "—"}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {variant.supplierBarcode || "—"}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {variant.color?.name || "—"}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {variant.size?.name || "—"}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {activeLot ? (
                        <div>
                          <p className="font-medium text-gray-900">
                            {activeLot.lotNumber}
                          </p>
                          <p className="mt-1 text-xs text-gray-500">
                            MRP {money(activeLot.mrp)} · POS {money(activeLot.posPrice)}
                          </p>
                          <p className="mt-1 text-xs text-gray-500">
                            Online {money(activeLot.onlinePrice)}
                          </p>
                        </div>
                      ) : (
                        <span className="text-gray-500">No active lot</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium">
                      {Number(availableQuantity).toLocaleString("en-IN", {
                        maximumFractionDigits: 3,
                      })}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-medium ${
                          variant.isActive
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {variant.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() =>
                            setVariantModal({
                              open: true,
                              variant,
                            })
                          }
                          className="rounded-lg p-2 text-blue-600 hover:bg-blue-50"
                        >
                          <FiEdit2 />
                        </button>

                        <button
                          type="button"
                          disabled={!variant.barcode}
                          title={variant.barcode ? "Reprint barcode label" : "This variant has no barcode"}
                          onClick={() => { setReprintVariant(variant); setReprintQuantity("1"); setReprintError(""); }}
                          className="rounded-lg p-2 text-primary-700 hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <FiTag />
                        </button>

                        <button
                          onClick={() => deleteVariant(variant)}
                          className="rounded-lg p-2 text-red-600 hover:bg-red-50"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {activeTab === "inventory" && (
        <>
        <section className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
          <div className="border-b border-gray-100 p-6">
            <div>
              <h2 className="text-xl font-bold">Store Inventory</h2>

              <p className="mt-1 text-sm text-gray-500">
                Inventory is maintained separately for every variant and store.
                Stock and lot prices can only be created by posting a GRN.
              </p>
            </div>
          </div>

          {inventory.length === 0 ? (
            <div className="p-10 text-center text-gray-500">
              No stock has been recorded for this product yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    {[
                      "Variant",
                      "Store",
                      "On Hand",
                      "Reserved",
                      "Available",
                      "Average Cost",
                    ].map((heading) => (
                      <th
                        key={heading}
                        className="px-6 py-4 text-left text-xs font-medium uppercase text-gray-500"
                      >
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {inventory.map((balance) => {
                    const variant = variants.find(
                      (item) => item.id === balance.variantId,
                    );

                    const onHand = Number(balance.quantityOnHand || 0);
                    const reserved = Number(balance.quantityReserved || 0);

                    return (
                      <tr key={balance.id} className="border-b border-gray-100">
                        <td className="px-6 py-4">
                          <p className="font-semibold">
                            {variant?.variantName || "Default"}
                          </p>
                          <p className="text-xs text-gray-500">
                            {variant?.sku || "—"}
                          </p>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {balance.store?.name || balance.storeId}
                        </td>
                        <td className="px-6 py-4">{onHand}</td>
                        <td className="px-6 py-4">{reserved}</td>
                        <td className="px-6 py-4">{onHand - reserved}</td>
                        <td className="px-6 py-4">
                          {money(balance.averageUnitCost)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="mt-6 overflow-hidden rounded-3xl border border-gray-100 bg-white">
          <div className="border-b border-gray-100 p-6">
            <h2 className="text-xl font-bold">Lot & Batch Traceability</h2>
            <p className="mt-1 text-sm text-gray-500">Every lot was created by a posted GRN. Search the Products page using a lot number or supplier batch number to find this product quickly.</p>
          </div>
          <div className="overflow-x-auto"><table className="w-full min-w-[1100px]"><thead><tr className="border-b bg-gray-50">{["Variant", "Lot / supplier batch", "Store", "Received", "Available", "Cost", "MRP / POS", "Online"].map((heading) => <th key={heading} className="px-6 py-4 text-left text-xs font-medium uppercase text-gray-500">{heading}</th>)}</tr></thead><tbody>{(product?.variants || []).flatMap((variant) => (variant.inventoryLots || []).map((lot) => ({ lot, variant }))).length ? (product?.variants || []).flatMap((variant) => (variant.inventoryLots || []).map((lot) => ({ lot, variant }))).map(({ lot, variant }) => <tr key={lot.id} className="border-b border-gray-100"><td className="px-6 py-4"><p className="font-semibold">{variant.variantName || "Default"}</p><p className="text-xs text-gray-500">{variant.sku}</p></td><td className="px-6 py-4"><p className="font-mono font-semibold text-primary-800">{lot.lotNumber}</p><p className="mt-1 text-xs text-gray-500">Batch: {lot.supplierBatchNumber || "—"}</p></td><td className="px-6 py-4 text-sm">{lot.store?.name || "—"}</td><td className="px-6 py-4 text-sm">{lot.receivedAt ? new Date(lot.receivedAt).toLocaleDateString("en-IN") : "—"}</td><td className="px-6 py-4">{Math.max(0, Number(lot.remainingQuantity || 0) - Number(lot.reservedQuantity || 0))}</td><td className="px-6 py-4">{money(lot.unitCost)}</td><td className="px-6 py-4"><p>{money(lot.mrp)}</p><p className="mt-1 text-xs text-gray-500">POS: {money(lot.posPrice)}</p></td><td className="px-6 py-4">{money(lot.onlinePrice)}</td></tr>) : <tr><td colSpan="8" className="px-6 py-12 text-center text-gray-500">No active purchase lots exist for this product.</td></tr>}</tbody></table></div>
        </section>
        </>
      )}

      {activeTab === "suppliers" && (
        <section className="rounded-3xl border border-gray-100 bg-white p-6">
          <h2 className="mb-5 text-xl font-bold">Linked Suppliers</h2>

          {suppliers.length === 0 ? (
            <p className="text-gray-500">No supplier is linked to this product.</p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {suppliers.map((item) => (
                <div
                  key={item.id}
                  className="rounded-2xl border border-gray-100 p-5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold">
                        {item.supplier?.displayName || item.supplier?.name}
                      </p>
                      <p className="mt-1 text-sm text-gray-500">
                        Supplier SKU: {item.supplierSku || "—"}
                      </p>
                    </div>

                    {item.isPreferred && (
                      <span className="rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
                        Preferred
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {activeTab === "images" && (
        <section className="rounded-3xl border border-gray-100 bg-white p-6">
          <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h2 className="text-xl font-bold">Product Images</h2>

              <p className="mt-1 text-sm text-gray-500">
                Upload up to 8 JPG, PNG, or WEBP images. The main image is used
                in the product cards and e-commerce website.
              </p>
            </div>

            <button
              onClick={saveImages}
              disabled={savingImages || uploadingImages}
              className="flex h-11 items-center justify-center gap-2 rounded-xl bg-primary-700 px-5 text-white hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FiSave />
              {savingImages ? "Saving…" : "Save Images"}
            </button>
          </div>

          {imageError && (
            <div className="mb-5 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
              {imageError}
            </div>
          )}

          <label
            className={`flex flex-col items-center justify-center rounded-2xl border-2 border-dashed p-8 text-center transition ${
              uploadingImages
                ? "cursor-wait border-primary-200 bg-primary-50"
                : "cursor-pointer border-gray-300 bg-gray-50 hover:border-primary-500 hover:bg-primary-50"
            }`}
          >
            <FiUpload className="mb-3 text-primary-700" size={32} />

            <p className="font-semibold text-gray-800">
              {uploadingImages ? "Uploading images…" : "Choose product images"}
            </p>

            <p className="mt-1 text-sm text-gray-500">
              JPG, PNG, WEBP · Maximum 5 MB each · Maximum 8 images
            </p>

            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              disabled={uploadingImages || images.length >= 8}
              onChange={uploadSelectedImages}
              className="hidden"
            />
          </label>

          {images.length === 0 ? (
            <div className="mt-6 rounded-2xl border border-dashed border-gray-300 p-10 text-center text-gray-500">
              No images have been added to this product.
            </div>
          ) : (
            <>
              <div className="mt-6 overflow-hidden rounded-3xl bg-gray-100">
                <img
                  src={getImageUrl(images[activeImage]?.storageKey)}
                  alt={images[activeImage]?.altText || product.name}
                  className="h-[360px] w-full object-cover"
                />
              </div>

              <div className="mt-5 grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {images.map((image, index) => (
                  <div
                    key={image.storageKey}
                    className={`overflow-hidden rounded-2xl border bg-white ${
                      activeImage === index
                        ? "border-primary-700 ring-2 ring-primary-100"
                        : "border-gray-200"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setActiveImage(index)}
                      className="relative block aspect-square w-full bg-gray-100"
                    >
                      <img
                        src={getImageUrl(image.storageKey)}
                        alt={image.altText || product.name}
                        className="h-full w-full object-cover"
                      />

                      {image.role === "THUMBNAIL" && (
                        <span className="absolute left-3 top-3 rounded-full bg-primary-700 px-3 py-1 text-xs font-semibold text-white">
                          Main image
                        </span>
                      )}

                      <span className="absolute inset-0 bg-black/0 transition hover:bg-black/10" />
                    </button>

                    <div className="space-y-3 p-4">
                      <input
                        value={image.altText || ""}
                        onChange={(event) =>
                          updateImageAltText(
                            image.storageKey,
                            event.target.value,
                          )
                        }
                        placeholder="Image description (optional)"
                        className="h-10 w-full rounded-lg border border-gray-200 px-3 text-sm outline-none focus:border-primary-700"
                      />

                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          disabled={image.role === "THUMBNAIL"}
                          onClick={() => setMainImage(image.storageKey)}
                          className="rounded-lg border border-primary-200 px-3 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50 disabled:cursor-default disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-400"
                        >
                          {image.role === "THUMBNAIL"
                            ? "Main image"
                            : "Make main"}
                        </button>

                        <button
                          type="button"
                          onClick={() => removeImage(image.storageKey)}
                          className="rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      )}

      {variantModal.open && (
        <ProductVariantModal
          productId={product.id}
          variant={variantModal.variant}
          onClose={() =>
            setVariantModal({
              open: false,
              variant: null,
            })
          }
          onSaved={async () => {
            setVariantModal({
              open: false,
              variant: null,
            });

            await loadProduct();
          }}
        />
      )}

      {reprintVariant && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-950/50 p-4">
          <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4"><div><p className="text-sm text-gray-500">Barcode labels</p><h2 className="mt-1 text-xl font-bold">Reprint {reprintVariant.variantName || reprintVariant.sku}</h2><p className="mt-2 text-sm text-gray-500">{reprintVariant.barcode}</p></div><button type="button" onClick={() => setReprintVariant(null)} className="rounded-xl p-2 text-gray-500 hover:bg-gray-100">×</button></div>
            {reprintError && <div className="mt-5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">{reprintError}</div>}
            <label className="mt-6 block text-sm font-medium text-gray-700">Number of labels</label>
            <input autoFocus type="number" min="1" max="500" value={reprintQuantity} onChange={(event) => setReprintQuantity(event.target.value)} className="mt-2 h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700" />
            <p className="mt-2 text-xs text-gray-500">Default is one label. The label uses the latest active lot MRP when one is available.</p>
            <div className="mt-6 flex justify-end gap-3"><button type="button" onClick={() => setReprintVariant(null)} className="h-11 rounded-xl border border-gray-200 px-5 font-medium">Cancel</button><button type="button" onClick={() => { try { const activeLot = lotSummary(reprintVariant).activeLot; printVariantBarcodeLabels({ product, variant: reprintVariant, mrp: activeLot?.mrp ?? null, quantity: reprintQuantity }); setReprintVariant(null); } catch (printError) { setReprintError(printError.message || "Could not prepare barcode labels."); } }} className="flex h-11 items-center gap-2 rounded-xl bg-primary-700 px-5 font-medium text-white"><FiTag /> Print labels</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon, color }) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-5">
      <div
        className={`mb-4 flex h-11 w-11 items-center justify-center rounded-xl ${color}`}
      >
        {icon}
      </div>

      <p className="text-sm text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase text-gray-500">{label}</p>
      <p className="mt-1 font-medium text-gray-900">{value || "—"}</p>
    </div>
  );
}

function Setting({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-gray-100 pb-3 text-sm last:border-0">
      <span className="text-gray-600">{label}</span>

      <span
        className={`rounded-full px-3 py-1 text-xs font-medium ${
          value ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
        }`}
      >
        {value ? "Enabled" : "Disabled"}
      </span>
    </div>
  );
}
